import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Field, Select } from "@/components/ui";
import { fetchStaffCourses } from "@/services/api/instructor";

type CourseSwitcherProps = {
  tenantSlug: string;
  courseId: string;
  /** The per-course page to stay on when switching. */
  section: "gradebook" | "analytics";
};

/**
 * Gradebook and analytics were reachable only from the builder, so comparing two
 * courses meant three clicks each way (CRS-009). This lists the staff member's
 * courses — the same list, and the same query cache, as the course list page —
 * and opens the same page for the one chosen. Hidden when there is nothing to
 * switch to.
 */
export function CourseSwitcher({ tenantSlug, courseId, section }: CourseSwitcherProps) {
  const t = useTranslations("instructorCourseTools");
  const navigate = useNavigate();
  const coursesQuery = useQuery({
    queryKey: ["instructor-courses", tenantSlug],
    queryFn: fetchStaffCourses,
  });

  const courses = coursesQuery.data ?? [];
  if (courses.length < 2) return null;

  return (
    <Field label={t("switchCourseLabel")} className="mb-6 max-w-sm">
      <Select
        data-testid="course-switcher"
        value={courseId}
        onChange={(event) =>
          navigate(`/t/${tenantSlug}/instructor/courses/${event.target.value}/${section}`)
        }
      >
        {courses.map((course) => (
          <option key={course.id} value={course.id}>
            {course.title}
          </option>
        ))}
      </Select>
    </Field>
  );
}
